import React, { forwardRef, useCallback, useImperativeHandle } from 'react';
import classNames from 'classnames';
import { t } from 'i18next';
import { Column } from '@prio365/prio365-react-library/lib/VirtualTable/types/Column';
import {
  RemainingAbsence,
  RemainingAbsenceV2,
} from '../../../models/RemainingAbsence';
import useFilterContext, {
  useFilterContextDataConvertion,
} from '../../../components/Filter/hooks/useFilterContext';
import FilterResultNoItemsScreen from '../../../components/Filter/FilterResultNoItemsScreen';
import { VirtualListItemOnRowProps } from '@prio365/prio365-react-library/lib/VirtualList/components/VirtualListItem';
import useContactsContext from '../../contacts/hooks/useContactsProvider';
import useOfficesContext from '../../companies/hooks/useOfficesContext';
import { makePrioStyles } from '../../../theme/utils';
import FilterContextVirtualTable from '../../../components/Filter/FilterContextVirtualTable';
import { sortContactsHelper } from '../../contacts/utils';
import { exportToCsv } from '../../../util/export';
import useExcelExport from '../../../hooks/useExcelExport';

const useStyles = makePrioStyles((theme) => ({
  root: {},
  cell: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
  },
  cellHorizontallyCentered: {
    display: 'flex',
    justifyContent: 'center',
    '& > span': {
      display: 'flex',
      justifyContent: 'center',
    },
  },
  marginRight: {
    marginRight: 10,
  },
  ellipsis: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'block',
  },
}));

export interface HRRemainingAbsencePageTableRef {
  getSelectedRemainingAbsences: () => RemainingAbsenceV2[];
  fetchRemainingAbsences: () => void;
}

interface HRRemainingAbsencePageTableProps {
  className?: string;
  tableId: string;
  onRowClick: (row: RemainingAbsence) => void;
  onRowSelectionChange: (rows: RemainingAbsenceV2[]) => void;
  selectedRemainingAbsences: RemainingAbsenceV2[];
}

export const HRRemainingAbsencePageTable = forwardRef(
  (
    props: HRRemainingAbsencePageTableProps,
    ref: React.Ref<HRRemainingAbsencePageTableRef>
  ) => {
    //#region ------------------------------ Defaults
    const {
      tableId,
      selectedRemainingAbsences,
      onRowClick,
      onRowSelectionChange,
    } = props;
    const classes = useStyles();

    const { getContactById } = useContactsContext();
    const { getOfficeById } = useOfficesContext();

    const excelExport = useExcelExport();
    //#endregion

    //#region ------------------------------ States / Attributes / Selectors
    const { data, isLoading, fetchSearch } =
      useFilterContext<RemainingAbsenceV2>();

    const remainingAbsences = useFilterContextDataConvertion(data, true).sort(
      (a, b) => a.year - b.year
    );

    //#endregion

    //#region ------------------------------ Methods / Handlers
    const handleOnRow: (item: RemainingAbsenceV2) => VirtualListItemOnRowProps =
      useCallback(
        (item) => {
          return {
            onClick: (e) => {
              if (onRowClick) {
                onRowClick(item);
              }
            },
          };
        },
        [onRowClick]
      );

    const onSelectionChange = (items: RemainingAbsenceV2[]) => {
      if (onRowSelectionChange) {
        onRowSelectionChange(items);
      }
    };

    const handleOnExportToExcelClick = useCallback(() => {
      const itemsToExport = (
        selectedRemainingAbsences.length > 0
          ? selectedRemainingAbsences
          : remainingAbsences
      ).map((holiday) => {
        return {
          [t(
            'absences:remainingAbsenceManagement.table.columnTitle.applicantId'
          )]: `${getContactById(holiday.employeeId)?.firstName} ${
            getContactById(holiday.employeeId)?.lastName
          }`,
          [t('absences:remainingAbsenceManagement.table.columnTitle.office')]:
            getOfficeById(holiday.officeId)?.name,
          [t('absences:remainingAbsenceManagement.table.columnTitle.year')]:
            holiday.year.toString(),
          [`${t(
            `absences:remainingAbsenceManagement.table.columnTitle.holidayEntitlements`
          )} (${t(`absences:absenceOverview.popOver.days`)})`]:
            `${(holiday.holidayEntitlement ?? 0).toLocaleString()}`,
          [`${t(
            `absences:remainingAbsenceManagement.table.columnTitle.remainingAbsenceDays`
          )} (${t(`absences:absenceOverview.popOver.days`)})`]:
            `${(holiday.remainingEntitlement ?? 0).toLocaleString()}`,
        };
      });
      excelExport(
        itemsToExport,
        t('absences:hrAbsenceMenu.navigation.remainingabsence'),
        t('absences:hrAbsenceMenu.navigation.remainingabsence')
      );
    }, [
      selectedRemainingAbsences,
      excelExport,
      remainingAbsences,
      getOfficeById,
      getContactById,
    ]);

    const handleOnExportToCsvClick = useCallback(() => {
      const headers = [
        t('absences:remainingAbsenceManagement.table.columnTitle.applicantId'),
        t('absences:remainingAbsenceManagement.table.columnTitle.office'),
        t('absences:remainingAbsenceManagement.table.columnTitle.year'),
        `${t(
          `absences:remainingAbsenceManagement.table.columnTitle.holidayEntitlements`
        )} (${t(`absences:absenceOverview.popOver.days`)})`,
        `${t(
          `absences:remainingAbsenceManagement.table.columnTitle.remainingAbsenceDays`
        )} (${t(`absences:absenceOverview.popOver.days`)})`,
      ];
      const itemsToExport = (
        selectedRemainingAbsences.length > 0
          ? selectedRemainingAbsences
          : remainingAbsences
      ).map((holiday) => [
        `${getContactById(holiday.employeeId)?.firstName} ${
          getContactById(holiday.employeeId)?.lastName
        }`,
        getOfficeById(holiday.officeId)?.name,
        holiday.year.toString(),
        `${(holiday.holidayEntitlement ?? '-')?.toLocaleString()}`,
        `${(holiday.remainingEntitlement ?? '-')?.toLocaleString()}`,
      ]);

      exportToCsv(
        headers,
        itemsToExport,
        t('absences:hrAbsenceMenu.navigation.remainingabsence')
      );
    }, [
      selectedRemainingAbsences,
      getOfficeById,
      remainingAbsences,
      getContactById,
    ]);

    //#endregion

    //#region ------------------------------ Effects
    useImperativeHandle(ref, () => ({
      getSelectedRemainingAbsences: () => {
        return selectedRemainingAbsences;
      },
      fetchRemainingAbsences: () => {
        fetchSearch();
      },
    }));
    //#endregion

    //#region ------------------------------ Column
    const columns: Column<RemainingAbsenceV2>[] = [
      {
        sortingFn: (rowA, rowB) =>
          sortContactsHelper(
            getContactById(rowA.employeeId),
            getContactById(rowB.employeeId)
          ),
        title: t(
          'absences:remainingAbsenceManagement.table.columnTitle.applicantId'
        ),
        id: 'employeeId',
        accessor: 'employeeId',
        Cell: (cellProps) => (
          <div
            className={classNames(classes.cell)}
            title={`${
              getContactById(cellProps.value)?.firstName
            } ${getContactById(cellProps.value)?.lastName}`}
          >
            <div className={classes.ellipsis}>
              {`${getContactById(cellProps.value)?.firstName} ${
                getContactById(cellProps.value)?.lastName
              }`}
            </div>
          </div>
        ),
        width: 32,
        alignSelf: true,
      },
      {
        sortingFn: (a, b) =>
          getOfficeById(a.officeId)?.name?.localeCompare(
            getOfficeById(b.officeId)?.name
          ),
        title: t(
          'absences:remainingAbsenceManagement.table.columnTitle.office'
        ),
        id: 'officeId',
        accessor: 'officeId',
        Cell: ({ originalData: { officeId } }) => getOfficeById(officeId)?.name,
        width: 20,
        className: classes.cell,
        alignSelf: true,
      },
      {
        sortingFn: (a, b) => a.year - b.year,
        title: t('absences:remainingAbsenceManagement.table.columnTitle.year'),
        id: 'year',
        accessor: 'year',
        Cell: ({ originalData: { year } }) => year,
        width: 16,
        className: classNames(
          classes.cellHorizontallyCentered,
          classes.marginRight,
          classes.cell
        ),
        alignSelf: true,
      },
      {
        sortingFn: (a, b) => a.holidayEntitlement - b.holidayEntitlement,
        title: t(
          'absences:remainingAbsenceManagement.table.columnTitle.holidayEntitlements'
        ),
        id: 'holidayEntitlement',
        accessor: 'holidayEntitlement',
        Cell: ({ originalData: { holidayEntitlement } }) => {
          const dayLabel = holidayEntitlement === 1 ? 'day' : 'days';
          return `${holidayEntitlement} ${t(`absences:absenceOverview.popOver.${dayLabel}`)}`;
        },
        width: 16,
        className: classNames(
          classes.cellHorizontallyCentered,
          classes.marginRight,
          classes.cell
        ),
        alignSelf: true,
      },
      {
        sortingFn: (a, b) => a.remainingEntitlement - b.remainingEntitlement,
        title: t(
          'absences:remainingAbsenceManagement.table.columnTitle.remainingAbsenceDays'
        ),
        id: 'remainingEntitlement',
        accessor: 'remainingEntitlement',
        Cell: ({ originalData: { remainingEntitlement } }) => {
          const dayLabel = remainingEntitlement === 1 ? 'day' : 'days';
          return `${remainingEntitlement} ${t(`absences:absenceOverview.popOver.${dayLabel}`)}`;
        },
        width: 16,
        className: classNames(
          classes.cellHorizontallyCentered,
          classes.marginRight,
          classes.cell
        ),
        alignSelf: true,
      },
    ];
    //#endregion

    return (
      <FilterContextVirtualTable<RemainingAbsenceV2>
        id={tableId}
        resizable="relative"
        rowsAreSelectable
        className={classes.root}
        data={remainingAbsences}
        noItemsScreen={<FilterResultNoItemsScreen />}
        columns={columns}
        loading={
          isLoading && {
            type: 'noItems',
          }
        }
        onRow={handleOnRow}
        onSelectionChange={onSelectionChange}
        onCheckEquality={(a, b) =>
          a.employeeId === b.employeeId && a.year === b.year
        }
        actionBarDefaultButtons={
          remainingAbsences?.length > 0
            ? [
                {
                  children: t('common:actions.csvExport'),
                  iconProp: 'file-arrow-down',
                  type: 'primary',
                  onClick: handleOnExportToCsvClick,
                },
                {
                  children: t('common:actions.excelExport'),
                  iconProp: 'file-excel',
                  onClick: handleOnExportToExcelClick,
                },
              ]
            : []
        }
        actionBarButtons={[
          {
            children: t('common:actions.excelExportSelection'),
            iconProp: 'file-excel',
            onClick: handleOnExportToExcelClick,
          },
          {
            children: t('common:actions.csvExportSelection'),
            iconProp: 'file-arrow-down',
            onClick: handleOnExportToCsvClick,
          },
        ]}
      />
    );
  }
);

export default HRRemainingAbsencePageTable;
